import { BUILDINGS, TOOLBAR } from '../core/defs';
import type { LevelDef } from '../core/levels';
import { buildReport, type RunReport } from '../core/report';
import { DT, DX, DY, type Building, type BuildingKind, type Dir, type ItemKind, type Source, type WorldEvent } from '../core/types';
import { BELT_LIKE, World, type ActionResult } from '../core/world';
import { sfx, type SfxName } from '../audio/sfx';
import { Minimap } from '../render/minimap';
import { Renderer, type ViewState } from '../render/renderer';
import { describe, toastWorthy } from '../ui/eventText';
import { Hud } from '../ui/hud';
import { nextStep, problems, runTool, usersCenter, type Blueprint, type CmdResult, type CommandHost } from './commands';
import { parseCommand, splitUtterance } from './parse';
import { recordRun, type Settings } from './progress';
import { VoiceController } from './voice';
import { ReviewController } from './review';

export interface SessionCallbacks {
  onEnd(report: RunReport, world: World): void;
  onQuit(): void;
  onRestart(): void;
  onSettings(): void;
}

const FX_SOUND: Partial<Record<string, SfxName>> = {
  remove: 'remove', flawed: 'flawed', corrupt: 'corrupt', zap: 'zap', glitchDie: 'glitchDie', spawn: 'glitchSpawn',
  rollback: 'rollback', trip: 'trip', secret: 'secret', research: 'research', nestDie: 'nestDie', protect: 'protect',
  backfire: 'backfire', approval: 'approval', denied: 'denied', patch: 'patch', drone: 'drone',
};

type Pointer = { id: number; x: number; y: number; sx: number; sy: number; button: number; type: string; t: number };

export class Session {
  readonly level: LevelDef;
  readonly world: World;
  readonly renderer: Renderer;
  readonly minimap: Minimap;
  readonly hud: Hud;
  readonly voice: VoiceController;
  readonly review: ReviewController | null = null;
  settings: Settings;
  view: ViewState;
  speed = 0;
  private resumeSpeed = 1;
  blueprint: Blueprint | null = null;
  copyMode = false;
  private cb: SessionCallbacks;
  private raf = 0;
  private last = 0;
  private acc = 0;
  private lastHud = 0;
  private lastMini = 0;
  private eventCursor = 0;
  private ended = false;
  private keys = new Set<string>();
  private pointers = new Map<number, Pointer>();
  private pinch: { d: number; cx: number; cy: number } | null = null;
  private drag: 'belt' | 'build' | 'pan' | 'remove' | 'copy' | null = null;
  private lastBuildTile = '';
  private seenProblems = new Set<string>();
  private cleanup: (() => void)[] = [];
  private canvas: HTMLCanvasElement;
  hoverWorld: { x: number; y: number } | null = null;
  /** Operator API subscribers. */
  readonly listeners = new Set<(e: WorldEvent) => void>();

  constructor(level: LevelDef, canvas: HTMLCanvasElement, hudRoot: HTMLElement, settings: Settings, cb: SessionCallbacks) {
    this.level = level;
    this.cb = cb;
    this.settings = settings;
    this.canvas = canvas;
    this.world = new World(level);
    this.renderer = new Renderer(canvas);
    // Frame the starting area: the level's first revealed circle, or the users.
    const start = level.features.find((f) => f.t === 'reveal');
    const u = usersCenter(this.world);
    const r = start && start.t === 'reveal' ? start.r : 14;
    this.renderer.cam.cx = start && start.t === 'reveal' ? start.x : u.x;
    this.renderer.cam.cy = start && start.t === 'reveal' ? start.y : u.y;
    const fit = Math.min(window.innerWidth / (2 * r + 4), (window.innerHeight - 150) / (2 * r + 4));
    this.renderer.cam.zoom = Math.max(14, Math.min(34, Math.round(fit)));
    this.view = {
      tool: null, toolDir: 0, hover: null, place: null, dragPath: null, removeRect: null, copyRect: null, selectedId: null,
      showLabels: settings.labels, stamp: null, planMode: false, reducedMotion: settings.reducedMotion, showDebt: false,
    };
    const mini = document.createElement('canvas');
    this.minimap = new Minimap(mini);
    this.hud = new Hud(hudRoot, this, mini);
    this.minimap.bind((x, y) => this.renderer.cam.focus(x, y));
    this.voice = new VoiceController(this);
    if (level.review) this.review = new ReviewController(this, level.review, hudRoot);
    // Plan-only: every placement is a ghost, so show it that way while placing.
    if (level.flags.planOnly) this.view.planMode = true;
    this.bindInput();
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  // ------------------------------------------------------------ command host

  host(src: Source): CommandHost {
    return {
      world: this.world,
      src,
      cursor: this.hoverWorld ? { x: Math.floor(this.hoverWorld.x), y: Math.floor(this.hoverWorld.y) } : null,
      focus: (x, y) => this.renderer.cam.focus(x, y),
      setSpeed: (n) => this.setSpeed(n),
      blueprint: this.blueprint,
      onNeeds: (needs) => {
        if (src === 'mouse' || src === 'keys' || src === 'command') this.hud.showPermission(needs, null);
      },
    };
  }

  runTool(name: string, args: Record<string, unknown>, src: Source): CmdResult {
    sfx.unlock();
    const r = runTool(this.host(src), name, args);
    const quiet = name === 'get_state' || name === 'focus' || name === 'set_speed' || src === 'api';
    if (!quiet) sfx.play(r.ok ? 'click' : 'error', { gain: 0.6 });
    return r;
  }

  /** Typed or spoken plain-language command. Handles "do X then Y". */
  exec(text: string, src: Source): { ok: boolean; message: string } {
    const parts = splitUtterance(text);
    if (!parts.length) return { ok: false, message: '' };
    const msgs: string[] = [];
    let ok = true;
    for (const p of parts) {
      const parsed = parseCommand(p);
      if (!parsed) {
        msgs.push(`Didn't catch "${p}". Type help for examples.`);
        ok = false;
        continue;
      }
      if ('help' in parsed) {
        msgs.push(parsed.help);
        continue;
      }
      if (parsed.tool === 'get_state') {
        const pr = problems(this.world);
        const g = this.world.goalProgress();
        msgs.push(`${g.label}: ${g.value}/${g.target}. Budget ${Math.floor(this.world.credits)}, trust ${Math.round(this.world.trust)}.${pr.length ? ` ${pr.slice(0, 2).join(' ')}` : ''}`);
        continue;
      }
      const r = this.runTool(parsed.tool, parsed.args, src);
      msgs.push(r.message);
      ok = ok && r.ok;
    }
    this.world.log({ type: 'command', text, src, ok });
    return { ok, message: msgs.join(' ') };
  }

  // ------------------------------------------------------------ state changes

  setSpeed(n: number) {
    if (this.world.outcome !== 'playing') return;
    if (n > 0) this.resumeSpeed = n;
    this.speed = n;
    this.hud.update();
  }

  togglePause() {
    this.setSpeed(this.speed === 0 ? this.resumeSpeed : 0);
  }

  /** Toolbar clicks toggle; hotkeys always pick, so a hint's "press 1" can't put belts down. Esc does that. */
  setTool(kind: BuildingKind | null, toggle = true) {
    if (kind && !this.world.allowed(kind)) return;
    this.view.tool = toggle && this.view.tool === kind ? null : kind;
    this.view.stamp = null;
    this.copyMode = false;
    this.view.dragPath = null;
    if (this.view.tool) this.select(null);
    this.updatePlace();
    this.hud.refreshToolbar();
    sfx.play('click', { gain: 0.4 });
  }

  /** R turns what's under the cursor; over open ground it turns what you're placing. */
  rotate() {
    const under = this.view.stamp ? undefined : this.hovered();
    if (!(under && BUILDINGS[under.kind].placeable) && (this.view.tool || this.view.stamp)) {
      this.view.toolDir = ((this.view.toolDir + 1) % 4) as Dir;
      sfx.play('rotate', { gain: 0.5 });
      return;
    }
    const b = under ?? (this.view.selectedId ? this.world.buildings.get(this.view.selectedId) : undefined);
    if (b && BUILDINGS[b.kind].placeable) {
      this.world.apply({ type: 'rotate', id: b.id }, 'keys');
      sfx.play('rotate', { gain: 0.5 });
    }
  }

  select(id: number | null) {
    this.view.selectedId = id;
    this.hud.openInspector(id);
  }

  togglePlan() {
    if (!this.level.flags.blueprints) {
      this.hud.toast('Planning with ghosts unlocks with drone hubs in level 6.', 'info');
      return;
    }
    this.view.planMode = !this.view.planMode;
    this.hud.toast(this.view.planMode ? 'Plan mode: placements become ghosts that drones build.' : 'Plan mode off.', 'info');
    this.hud.refreshToolbar();
  }

  startCopy() {
    if (!this.level.flags.blueprints) {
      this.hud.toast('Blueprints unlock in level 6.', 'info');
      return;
    }
    this.copyMode = !this.copyMode;
    this.view.tool = null;
    this.view.stamp = null;
    this.hud.toast(this.copyMode ? 'Drag over a working layout to copy it.' : 'Copy cancelled.', 'info');
    this.hud.refreshToolbar();
  }

  startStamp() {
    if (!this.blueprint) {
      this.hud.toast('Nothing copied yet. Press B and drag over a layout.', 'info');
      return;
    }
    this.view.tool = null;
    this.view.stamp = this.view.stamp ? null : this.blueprint;
    this.hud.refreshToolbar();
  }

  escape() {
    if (this.hud.closeTopmost()) return;
    if (this.view.tool || this.view.stamp || this.copyMode) {
      this.view.tool = null;
      this.view.stamp = null;
      this.copyMode = false;
      this.view.dragPath = null;
      this.hud.refreshToolbar();
      return;
    }
    if (this.view.selectedId) {
      this.select(null);
      return;
    }
    this.hud.openPause();
  }

  // ------------------------------------------------------------ placement

  private toolOrigin(kind: BuildingKind, fx: number, fy: number) {
    const def = BUILDINGS[kind];
    if (def.w === 1) return { x: Math.floor(fx), y: Math.floor(fy) };
    return { x: Math.round(fx - def.w / 2), y: Math.round(fy - def.h / 2) };
  }

  private updatePlace() {
    const h = this.hoverWorld;
    if (!h) {
      this.view.hover = null;
      this.view.place = null;
      return;
    }
    this.view.hover = { x: Math.floor(h.x), y: Math.floor(h.y) };
    this.view.place = this.view.tool ? this.toolOrigin(this.view.tool, h.x, h.y) : null;
  }

  private handleResult(r: ActionResult, retry?: () => void) {
    if (r.ok) return true;
    if (r.needs) {
      this.hud.showPermission(r.needs, retry ?? null);
      return false;
    }
    if (r.reason) this.hud.flash(r.reason);
    sfx.play('error', { gain: 0.5 });
    return false;
  }

  private placeAtCursor() {
    const kind = this.view.tool;
    const p = this.view.place;
    if (!kind || !p) return;
    const key = `${p.x},${p.y}`;
    if (key === this.lastBuildTile) return;
    this.lastBuildTile = key;
    const dir = this.view.toolDir;
    const ghost = this.view.planMode;
    const attempt = () => this.world.apply({ type: 'place', kind, x: p.x, y: p.y, dir, ghost }, 'mouse');
    const r = attempt();
    if (this.handleResult(r, () => this.handleResult(attempt()))) {
      sfx.play(kind === 'belt' || kind === 'firewall' ? 'placeBelt' : 'place');
      const b = r.ids?.length ? this.world.buildings.get(r.ids[0]) : undefined;
      if (b) this.warnDeadEnd(b);
    }
  }

  private extendBeltDrag(tx: number, ty: number) {
    const path = this.view.dragPath;
    if (!path) return;
    // A machine tile can only end a drag. Once the cursor moves on, route around
    // it, so cutting across a rack's corner on the way to a forge doesn't aim the belt at the rack.
    const solid = (x: number, y: number) => {
      const b = this.world.at(x, y);
      return !!b && !BELT_LIKE.has(b.kind) && (x !== tx || y !== ty);
    };
    while (path.length > 1 && solid(path[path.length - 1].x, path[path.length - 1].y)) path.pop();
    let last = path[path.length - 1];
    let guard = 0;
    while ((last.x !== tx || last.y !== ty) && guard++ < 400) {
      const sx = Math.sign(tx - last.x);
      const sy = Math.sign(ty - last.y);
      const xFirst = sx !== 0 && (Math.abs(tx - last.x) >= Math.abs(ty - last.y) || sy === 0);
      let nx = xFirst ? last.x + sx : last.x;
      let ny = xFirst ? last.y : last.y + sy;
      if (solid(nx, ny) && sx !== 0 && sy !== 0) {
        const ax = xFirst ? last.x : last.x + sx;
        const ay = xFirst ? last.y + sy : last.y;
        if (!solid(ax, ay)) [nx, ny] = [ax, ay];
      }
      const dir = (nx > last.x ? 0 : nx < last.x ? 2 : ny > last.y ? 1 : 3) as Dir;
      if (path.length >= 2 && path[path.length - 2].x === nx && path[path.length - 2].y === ny) {
        path.pop();
      } else {
        last.dir = dir;
        path.push({ x: nx, y: ny, dir });
      }
      last = path[path.length - 1];
    }
  }

  private commitBelts() {
    const path = this.view.dragPath;
    this.view.dragPath = null;
    if (!path?.length) return;
    const items: { kind: BuildingKind; x: number; y: number; dir: Dir }[] = [];
    for (const s of path) {
      const existing = this.world.at(s.x, s.y);
      if (existing?.kind === 'belt' || existing?.kind === 'breaker') {
        let guard = 0;
        while (existing.dir !== s.dir && guard++ < 4) this.world.apply({ type: 'rotate', id: existing.id }, 'mouse');
        continue;
      }
      if (existing) continue;
      items.push({ kind: 'belt', x: s.x, y: s.y, dir: s.dir });
    }
    if (path.length === 1 && !items.length) return;
    if (!items.length) return;
    this.view.toolDir = path[path.length - 1].dir;
    const attempt = () => this.world.apply({ type: 'placeMany', items, ghost: this.view.planMode }, 'mouse');
    const r = attempt();
    if (this.handleResult(r, () => this.handleResult(attempt()))) {
      sfx.play('placeBelt');
      if (r.detail) this.hud.flash(r.detail);
      // The drag may end on a machine; the belt that matters is the last one laid or re-aimed.
      for (let i = path.length - 1; i >= 0; i--) {
        const b = this.world.at(path[i].x, path[i].y);
        if (b && BELT_LIKE.has(b.kind)) {
          this.warnDeadEnd(b);
          break;
        }
      }
    }
  }

  /** Training levels: catch a belt or machine aimed at something that never takes work, the moment it's placed. */
  private warnDeadEnd(b: Building) {
    const belt = BELT_LIKE.has(b.kind);
    if (this.level.flags.diagnoses === false || b.ghost || !(belt || this.world.isProducer(b))) return;
    const ports = belt ? [[b.x + DX[b.dir], b.y + DY[b.dir]]] : this.world.outputPorts(b).front;
    const faced = ports.map(([x, y]) => this.world.at(x, y));
    const kinds: ItemKind[] = ['request', 'context', 'spec', 'draft', 'build', 'bug'];
    const takesNothing = (t: Building | undefined) =>
      !!t && !t.ghost && !BELT_LIKE.has(t.kind) && t.kind !== 'router' && !kinds.some((k) => this.world.accepts(t, k));
    if (!faced.length || !faced.every(takesNothing)) return;
    const t = faced[0]!;
    const who = belt ? 'This belt' : `${b.label}\u2019s arrow`;
    const fix = belt ? '' : ` Hover ${b.label} and press R to turn it.`;
    this.hud.flash(t.kind === 'rack'
      ? `${who} points into ${t.label}. Racks power machines inside their dashed circle; they don\u2019t take work.${fix}`
      : `${who} points into ${t.label}, which doesn\u2019t take work.${fix}`);
  }

  private removeAt(x: number, y: number) {
    const b = this.world.at(x, y);
    if (!b || !BUILDINGS[b.kind].placeable) return;
    this.world.apply({ type: 'remove', ids: [b.id] }, 'mouse');
    if (this.view.selectedId === b.id) this.select(null);
    sfx.play('remove');
  }

  private removeRect(x0: number, y0: number, x1: number, y1: number) {
    const ids = new Set<number>();
    for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++) {
      for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++) {
        const b = this.world.at(x, y);
        if (b && BUILDINGS[b.kind].placeable) ids.add(b.id);
      }
    }
    if (!ids.size) return;
    this.world.apply({ type: 'remove', ids: [...ids] }, 'mouse');
    sfx.play('remove');
  }

  private copyRect(x0: number, y0: number, x1: number, y1: number) {
    const xa = Math.min(x0, x1);
    const ya = Math.min(y0, y1);
    const xb = Math.max(x0, x1);
    const yb = Math.max(y0, y1);
    const seen = new Set<number>();
    const items: Blueprint['items'] = [];
    for (let y = ya; y <= yb; y++) {
      for (let x = xa; x <= xb; x++) {
        const b = this.world.at(x, y);
        if (!b || seen.has(b.id) || !BUILDINGS[b.kind].placeable) continue;
        if (b.x < xa || b.y < ya || b.x + b.w - 1 > xb || b.y + b.h - 1 > yb) continue;
        seen.add(b.id);
        items.push({ kind: b.kind, dx: b.x - xa, dy: b.y - ya, dir: b.dir });
      }
    }
    this.copyMode = false;
    if (!items.length) {
      this.hud.toast('Nothing buildable inside that box.', 'info');
      return;
    }
    this.blueprint = { name: 'Blueprint', w: xb - xa + 1, h: yb - ya + 1, items };
    this.view.stamp = this.blueprint;
    this.world.log({ type: 'note', text: `Copied a blueprint of ${items.length} pieces`, src: 'mouse' });
    this.hud.toast(`Copied ${items.length} pieces. Click to stamp ghosts; drone hubs build them. Ctrl+V stamps later.`, 'good');
    this.hud.refreshToolbar();
  }

  private stampAtCursor() {
    const bp = this.view.stamp;
    const h = this.view.hover;
    if (!bp || !h) return;
    const ox = h.x - Math.floor(bp.w / 2);
    const oy = h.y - Math.floor(bp.h / 2);
    const r = this.world.apply({ type: 'placeMany', ghost: true, items: bp.items.map((it) => ({ kind: it.kind, x: ox + it.dx, y: oy + it.dy, dir: it.dir })) }, 'mouse');
    if (this.handleResult(r)) {
      sfx.play('place');
      if (r.detail) this.hud.flash(r.detail);
      if (!this.world.buildings.size || ![...this.world.buildings.values()].some((b) => b.kind === 'drones' && !b.ghost)) {
        this.hud.flash('Ghosts placed. Nothing builds them until a drone hub is in range.');
      }
    }
  }

  hovered() {
    const h = this.view.hover;
    return h ? this.world.at(h.x, h.y) : undefined;
  }

  private clickNoTool() {
    const h = this.view.hover;
    if (!h) return;
    const b = this.world.at(h.x, h.y);
    if (this.review?.marking) {
      if (b && b.kind !== 'users') {
        const r = this.world.apply({ type: 'mark', id: b.id }, 'mouse');
        if (!r.ok && r.reason) this.hud.flash(r.reason);
        else sfx.play(this.world.marks.has(b.id) ? 'place' : 'remove', { gain: 0.5 });
        this.review.tick();
      }
      return;
    }
    if (b && this.world.revealed(h.x, h.y)) {
      if (b.kind === 'monolith') {
        this.hud.showPermission({ special: 'monolith', label: this.world.specialLabel('monolith'), x: b.x + 3, y: b.y + 3 }, null);
        return;
      }
      this.select(b.id);
      sfx.play('click', { gain: 0.35 });
      return;
    }
    const z = this.world.zoneOf(h.x, h.y);
    if (z && z.state !== 'approved' && z.state !== 'claimed' && this.world.revealed(h.x, h.y)) {
      this.hud.showPermission({ special: `zone:${z.id}`, label: this.world.specialLabel(`zone:${z.id}`), x: h.x, y: h.y }, null);
      return;
    }
    this.select(null);
  }

  // ------------------------------------------------------------ input

  private bindInput() {
    const c = this.canvas;
    const cam = this.renderer.cam;
    const on = <K extends keyof WindowEventMap>(t: EventTarget, type: K | string, fn: (e: never) => void, opts?: AddEventListenerOptions) => {
      t.addEventListener(type, fn as EventListener, opts);
      this.cleanup.push(() => t.removeEventListener(type, fn as EventListener, opts));
    };
    const tileOf = (sx: number, sy: number) => {
      const [wx, wy] = cam.toWorld(sx, sy);
      return { wx, wy, tx: Math.floor(wx), ty: Math.floor(wy) };
    };
    const setHover = (sx: number, sy: number) => {
      const t = tileOf(sx, sy);
      this.hoverWorld = { x: t.wx, y: t.wy };
      this.updatePlace();
      return t;
    };

    on(c, 'contextmenu', (e: MouseEvent) => e.preventDefault());

    on(c, 'pointerdown', (e: PointerEvent) => {
      sfx.unlock();
      this.hud.closeTip();
      c.setPointerCapture(e.pointerId);
      const p: Pointer = { id: e.pointerId, x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, button: e.button, type: e.pointerType, t: performance.now() };
      this.pointers.set(e.pointerId, p);
      if (this.pointers.size === 2) {
        // Pinch: cancel whatever the first finger started.
        const [a, b] = [...this.pointers.values()];
        this.pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
        this.drag = null;
        this.view.dragPath = null;
        return;
      }
      const t = setHover(e.clientX, e.clientY);
      if (e.button === 1 || (e.button === 0 && this.keys.has(' '))) {
        this.drag = 'pan';
        return;
      }
      if (e.button === 2) {
        this.drag = 'remove';
        this.view.removeRect = { x0: t.tx, y0: t.ty, x1: t.tx, y1: t.ty };
        return;
      }
      if (this.copyMode) {
        this.drag = 'copy';
        this.view.copyRect = { x0: t.tx, y0: t.ty, x1: t.tx, y1: t.ty };
        return;
      }
      if (this.view.stamp) {
        this.stampAtCursor();
        return;
      }
      if (this.view.tool === 'belt') {
        this.drag = 'belt';
        this.view.dragPath = [{ x: t.tx, y: t.ty, dir: this.view.toolDir }];
        return;
      }
      if (this.view.tool) {
        this.drag = 'build';
        this.lastBuildTile = '';
        this.placeAtCursor();
        return;
      }
      // No tool: a drag pans, a tap selects (decided on pointerup).
      this.drag = 'pan';
    });

    on(c, 'pointermove', (e: PointerEvent) => {
      const p = this.pointers.get(e.pointerId);
      const prevX = p?.x ?? e.clientX;
      const prevY = p?.y ?? e.clientY;
      if (p) {
        p.x = e.clientX;
        p.y = e.clientY;
      }
      if (this.pinch && this.pointers.size >= 2) {
        const [a, b] = [...this.pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        const cx = (a.x + b.x) / 2;
        const cy = (a.y + b.y) / 2;
        cam.zoomAt(d / Math.max(1, this.pinch.d), cx, cy);
        cam.pan(-(cx - this.pinch.cx) / cam.zoom, -(cy - this.pinch.cy) / cam.zoom);
        this.pinch = { d, cx, cy };
        return;
      }
      const t = setHover(e.clientX, e.clientY);
      this.hud.updateHoverTip(e.clientX, e.clientY);
      if (!p) return;
      switch (this.drag) {
        case 'pan':
          cam.pan(-(e.clientX - prevX) / cam.zoom, -(e.clientY - prevY) / cam.zoom);
          break;
        case 'belt':
          this.extendBeltDrag(t.tx, t.ty);
          break;
        case 'build':
          if (this.view.tool === 'firewall' || this.view.tool === 'breaker' || this.view.tool === 'router') this.placeAtCursor();
          break;
        case 'remove':
          if (this.view.removeRect) {
            this.view.removeRect.x1 = t.tx;
            this.view.removeRect.y1 = t.ty;
          }
          break;
        case 'copy':
          if (this.view.copyRect) {
            this.view.copyRect.x1 = t.tx;
            this.view.copyRect.y1 = t.ty;
          }
          break;
      }
    });

    const end = (e: PointerEvent) => {
      const p = this.pointers.get(e.pointerId);
      this.pointers.delete(e.pointerId);
      if (this.pinch) {
        if (this.pointers.size < 2) this.pinch = null;
        this.drag = null;
        return;
      }
      if (!p) return;
      const moved = Math.hypot(e.clientX - p.sx, e.clientY - p.sy) > 6;
      switch (this.drag) {
        case 'belt':
          this.commitBelts();
          break;
        case 'remove': {
          const r = this.view.removeRect;
          this.view.removeRect = null;
          if (r) {
            if (r.x0 === r.x1 && r.y0 === r.y1) this.removeAt(r.x0, r.y0);
            else this.removeRect(r.x0, r.y0, r.x1, r.y1);
          }
          break;
        }
        case 'copy': {
          const r = this.view.copyRect;
          this.view.copyRect = null;
          if (r) this.copyRect(r.x0, r.y0, r.x1, r.y1);
          break;
        }
        case 'pan':
          if (!moved && p.button === 0) this.clickNoTool();
          break;
      }
      this.drag = null;
    };
    on(c, 'pointerup', end);
    on(c, 'pointercancel', end);
    on(c, 'pointerleave', () => {
      if (!this.drag) {
        this.hoverWorld = null;
        this.updatePlace();
        this.hud.closeTip();
      }
    });

    on(c, 'wheel', (e: WheelEvent) => {
      e.preventDefault();
      const trackpadPan = !e.ctrlKey && e.deltaMode === 0 && (Math.abs(e.deltaX) > 0.5 || (Math.abs(e.deltaY) < 50 && !Number.isInteger(e.deltaY)));
      if (trackpadPan) cam.pan(e.deltaX / cam.zoom, e.deltaY / cam.zoom);
      else cam.zoomAt(Math.exp(-e.deltaY * (e.ctrlKey ? 0.012 : 0.0016)), e.clientX, e.clientY);
      setHover(e.clientX, e.clientY);
    }, { passive: false });

    on(window, 'resize', () => this.renderer.resize());

    on(window, 'keydown', (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (typing) {
        if (e.key === 'Escape') this.hud.closeCmd();
        return;
      }
      sfx.unlock();
      const k = e.key;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (k === 'z' || k === 'Z')) {
        e.preventDefault();
        const r = this.world.apply({ type: 'undo' }, 'keys');
        if (r.ok) sfx.play('remove', { gain: 0.6 });
        return;
      }
      if (mod && (k === 'v' || k === 'V')) {
        e.preventDefault();
        this.startStamp();
        return;
      }
      if (mod) return;
      if (this.hud.modalOpen() && k !== 'Escape') return;
      this.keys.add(k === ' ' ? ' ' : k.toLowerCase());
      if (k === 'Escape') return this.escape();
      if (k === 'Enter' || k === '/') {
        e.preventDefault();
        return this.hud.openCmd();
      }
      if (k === ' ') {
        e.preventDefault();
        return this.togglePause();
      }
      const lower = k.toLowerCase();
      const toolKey = TOOLBAR.find((t) => (BUILDINGS[t].hotkey ?? '').toLowerCase() === lower && this.world.allowed(t));
      if (toolKey && !['w', 'a', 's', 'd'].includes(lower)) return this.setTool(toolKey, false);
      switch (lower) {
        case 'r':
          return this.rotate();
        case 'q': {
          const b = this.hovered();
          if (b && BUILDINGS[b.kind].placeable && this.world.allowed(b.kind)) {
            this.view.tool = b.kind;
            this.view.toolDir = b.dir;
            this.updatePlace();
            this.hud.refreshToolbar();
          }
          return;
        }
        case 'x':
        case 'delete':
        case 'backspace': {
          const h = this.view.hover;
          if (h) this.removeAt(h.x, h.y);
          return;
        }
        case 'v':
          return void this.voice.toggle();
        case 't':
          return this.hud.toggleSide('research');
        case 'e':
          return this.hud.toggleSide('log');
        case 'l':
          this.view.showLabels = !this.view.showLabels;
          this.settings.labels = this.view.showLabels;
          return this.hud.refreshControls();
        case 'm':
          sfx.setMuted(!sfx.muted);
          this.hud.toast(sfx.muted ? 'Sound off' : 'Sound on', 'info');
          return this.hud.refreshControls();
        case 'h':
        case '?':
          return this.hud.showHelp();
        case 'b':
          return this.startCopy();
        case 'p':
          return this.togglePlan();
        case 'o':
          this.view.showDebt = !this.view.showDebt;
          return this.hud.toast(this.view.showDebt ? 'Showing tech debt' : 'Tech debt overlay off', 'info');
        case '.':
        case '>':
          return this.setSpeed(this.speed >= 4 ? 4 : this.speed === 0 ? 1 : this.speed * 2);
        case ',':
        case '<':
          return this.setSpeed(this.speed <= 1 ? 1 : this.speed / 2);
        case '=':
        case '+':
          return cam.zoomAt(1.2, cam.vw / 2, cam.vh / 2);
        case '-':
        case '_':
          return cam.zoomAt(1 / 1.2, cam.vw / 2, cam.vh / 2);
      }
    });
    on(window, 'keyup', (e: KeyboardEvent) => {
      this.keys.delete(e.key === ' ' ? ' ' : e.key.toLowerCase());
    });
    on(window, 'blur', () => this.keys.clear());
  }

  private panKeys(dt: number) {
    const cam = this.renderer.cam;
    let dx = 0;
    let dy = 0;
    if (this.keys.has('w') || this.keys.has('arrowup')) dy -= 1;
    if (this.keys.has('s') || this.keys.has('arrowdown')) dy += 1;
    if (this.keys.has('a') || this.keys.has('arrowleft')) dx -= 1;
    if (this.keys.has('d') || this.keys.has('arrowright')) dx += 1;
    if (dx || dy) {
      const sp = (900 / cam.zoom) * dt;
      cam.pan(dx * sp, dy * sp);
      if (this.hoverWorld) {
        this.hoverWorld.x += dx * sp;
        this.hoverWorld.y += dy * sp;
        this.updatePlace();
      }
    }
  }

  // ------------------------------------------------------------ loop

  private frame = (now: number) => {
    const dt = Math.min(0.1, Math.max(0, (now - this.last) / 1000));
    this.last = now;
    const w = this.world;
    if (this.speed > 0 && w.outcome === 'playing' && !this.hud.modalOpen()) {
      this.acc += dt * this.speed;
      let steps = 0;
      while (this.acc >= DT && steps < 24) {
        w.step();
        this.acc -= DT;
        steps++;
      }
      if (steps >= 24) this.acc = 0;
    }
    this.drainFx();
    this.drainEvents();
    this.panKeys(dt);
    this.renderer.render(w, this.view, this.speed > 0 ? this.acc / DT : 0, dt);
    if (now - this.lastHud > 125) {
      this.lastHud = now;
      this.hud.update();
      this.review?.tick();
      this.surfaceProblems();
      const corrupted = [...w.buildings.values()].filter((b) => b.corrupted).length;
      sfx.setTension(Math.min(1, w.glitches.length / 25 + corrupted / 5));
    }
    if (now - this.lastMini > 250) {
      this.lastMini = now;
      this.minimap.draw(w, this.renderer.cam, now);
    }
    if (w.outcome !== 'playing' && !this.ended) this.finish();
    this.raf = requestAnimationFrame(this.frame);
  };

  private drainFx() {
    const w = this.world;
    if (!w.fx.length) return;
    for (const f of w.fx) {
      this.renderer.addFx(f, this.view.reducedMotion);
      if (f.type === 'release') sfx.play(w.streak > 0 && w.streak % 25 === 0 ? 'releaseBig' : 'release', { pitch: Math.min(12, Math.floor(w.streak / 6)), gain: 0.55 });
      else if (f.type === 'place' || f.type === 'remove') continue;
      else {
        const s = FX_SOUND[f.type];
        if (s) sfx.play(s, { gain: f.type === 'zap' ? 0.35 : 0.8 });
      }
    }
    w.fx.length = 0;
  }

  private drainEvents() {
    const w = this.world;
    while (this.eventCursor < w.events.length) {
      const e = w.events[this.eventCursor++];
      for (const fn of this.listeners) {
        try {
          fn(e);
        } catch (err) {
          console.warn('blastRadius.on listener failed', err);
        }
      }
      if (toastWorthy(e)) {
        const d = describe(e, w);
        if (d) this.hud.toast(d.text, d.tone === 'act' ? 'warn' : d.tone);
      }
      if (e.type === 'perturbation' && e.text) {
        this.hud.banner(String(e.text));
        sfx.play('incident', { gain: 0.7 });
        this.voice.context(`Something changed: ${e.text}`);
      }
      if (e.type === 'incident' && e.cause !== 'flawed-release' && e.cause !== 'lost-update') this.voice.context(`Alert: ${describe(e, w)?.text ?? 'incident'}.`);
      if (e.type === 'approval') this.voice.context(`The review board ${e.approved ? 'approved' : 'denied'}: ${e.label}.`);
      if (e.type === 'secret') this.voice.context(`The player found ${e.name}.`);
    }
    this.hud.logChanged();
  }

  private surfaceProblems() {
    // While the coach line shows the next step, the goal card lists problems under it; toasts would repeat them.
    if (nextStep(this.world)) return;
    for (const p of problems(this.world).slice(0, 4)) {
      const key = p.replace(/\d+/g, '#');
      if (this.seenProblems.has(key)) continue;
      this.seenProblems.add(key);
      this.hud.toast(p, 'warn');
    }
  }

  private finish() {
    this.ended = true;
    const w = this.world;
    sfx.play(w.outcome === 'won' ? 'win' : 'lose');
    this.voice.stop();
    this.view.tool = null;
    window.setTimeout(() => {
      const report = buildReport(w);
      if (!w.level.part) recordRun(w.level.id, { stars: report.stars, seconds: report.metrics.seconds, agency: report.agency, safety: report.safety, won: w.outcome === 'won' });
      this.cb.onEnd(report, w);
    }, 1300);
  }

  begin() {
    if (this.review) {
      this.review.begin();
      return;
    }
    this.setSpeed(1);
    this.hud.toast(this.level.premise, 'info');
  }

  quit() {
    this.cb.onQuit();
  }

  restart() {
    this.cb.onRestart();
  }

  openSettings() {
    this.cb.onSettings();
  }

  endRun() {
    this.world.forfeit();
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    for (const f of this.cleanup) f();
    this.cleanup = [];
    this.voice.stop();
    this.review?.destroy();
    this.hud.destroy();
  }
}
